/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import { useEffect, useMemo, useState } from 'react';
import axios from '../api/axios';
import { resolveCoreApiPath } from '../utils/coreAssetUrl';
import { CompanyScopeChangedError, registerCompanyScopeInvalidator } from '../utils/company-scope-lifecycle.utils';

export type UserGroupDirectoryKind = 'user' | 'group' | 'systemGroup';

export interface UserGroupDirectoryItem {
  id: string;
  type: UserGroupDirectoryKind;
  name: string;
  email?: string;
  code?: string;
  description?: string;
  memberCount?: number;
}

interface DirectoryCacheEntry {
  expiresAt: number;
  items: UserGroupDirectoryItem[];
}

interface UseUserGroupDirectoryOptions {
  companyId: string | number | null | undefined;
  language?: string;
  includeSystem?: boolean;
  enabled?: boolean;
}

const DIRECTORY_CACHE_TTL_MS = 30_000;
const directoryCache = new Map<string, DirectoryCacheEntry>();
const directoryRequests = new Map<string, Promise<UserGroupDirectoryItem[]>>();
let directoryGeneration = 0;
registerCompanyScopeInvalidator(() => {
  directoryGeneration += 1;
  directoryCache.clear();
  directoryRequests.clear();
});

function normalizeKind(value: unknown): UserGroupDirectoryKind | null {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'user') return 'user';
  if (normalized === 'group') return 'group';
  if (normalized === 'systemgroup') return 'systemGroup';
  return null;
}

export function normalizeUserGroupDirectoryPayload(payload: unknown): UserGroupDirectoryItem[] {
  const root = (payload as any)?.data ?? payload;
  const list = Array.isArray(root) ? root : (Array.isArray((root as any)?.data) ? (root as any).data : []);

  return list.flatMap((item: any) => {
    const id = String(item?.id ?? '').trim();
    const type = normalizeKind(item?.type);
    const name = String(item?.name ?? item?.email ?? '').trim();
    if (!id || !type || !name) return [];
    return [{
      id,
      type,
      name,
      ...(item?.email ? { email: String(item.email) } : {}),
      ...(item?.code ? { code: String(item.code) } : {}),
      ...(item?.description ? { description: String(item.description) } : {}),
      ...(Number.isFinite(Number(item?.memberCount))
        ? { memberCount: Number(item.memberCount) }
        : {}),
    }];
  });
}

export function getUserGroupDirectoryCacheKey({
  companyId,
  language = 'tr',
  includeSystem = false,
}: Pick<UseUserGroupDirectoryOptions, 'companyId' | 'language' | 'includeSystem'>): string {
  return [
    String(companyId ?? '').trim(),
    String(language || 'tr').toLowerCase(),
    includeSystem ? 'system' : 'company',
  ].join(':');
}

export function invalidateUserGroupDirectory(companyId?: string | number | null): void {
  const companyPrefix = companyId == null ? null : `${String(companyId).trim()}:`;
  for (const key of directoryCache.keys()) {
    if (companyPrefix == null || key.startsWith(companyPrefix)) directoryCache.delete(key);
  }
}

async function loadDirectory(
  key: string,
  language: string,
  includeSystem: boolean,
): Promise<UserGroupDirectoryItem[]> {
  const cached = directoryCache.get(key);
  if (cached && cached.expiresAt > Date.now()) return cached.items;

  const existing = directoryRequests.get(key);
  if (existing) return existing;
  const generation = directoryGeneration;

  const currentApplicationCode = String(import.meta.env.VITE_APPLICATION_CODE ?? '').trim().toLowerCase();
  const lookupPath = currentApplicationCode === 'core'
    ? '/api/lookup/users-and-groups'
    : '/api/core/lookup/users-and-groups';
  const request = axios.get(
    resolveCoreApiPath(lookupPath),
    {
      params: includeSystem ? { includeSystem: true } : undefined,
      headers: { 'Accept-Language': language, 'X-Company-Id': key.split(':')[0] },
    },
  ).then((response) => {
    if (generation !== directoryGeneration) throw new CompanyScopeChangedError();
    const items = normalizeUserGroupDirectoryPayload(response.data);
    directoryCache.set(key, {
      items,
      expiresAt: Date.now() + DIRECTORY_CACHE_TTL_MS,
    });
    return items;
  }).finally(() => {
    if (directoryRequests.get(key) === request) directoryRequests.delete(key);
  });

  directoryRequests.set(key, request);
  return request;
}

export function useUserGroupDirectory({
  companyId,
  language = 'tr',
  includeSystem = false,
  enabled = true,
}: UseUserGroupDirectoryOptions) {
  const cacheKey = useMemo(
    () => getUserGroupDirectoryCacheKey({ companyId, language, includeSystem }),
    [companyId, includeSystem, language],
  );
  const [items, setItems] = useState<UserGroupDirectoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let active = true;
    const hasCompany = String(companyId ?? '').trim().length > 0;
    setItems([]);
    setError(null);
    if (!enabled || !hasCompany) {
      setLoading(false);
      return () => { active = false; };
    }

    const cached = directoryCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      setItems(cached.items);
      setLoading(false);
      return () => { active = false; };
    }

    setLoading(true);
    void loadDirectory(cacheKey, language, includeSystem)
      .then((nextItems) => {
        if (active) setItems(nextItems);
      })
      .catch((requestError) => {
        if (active) {
          setItems([]);
          setError(requestError);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [cacheKey, companyId, enabled, includeSystem, language]);

  return { items, loading, error };
}
